import { isNotFoundError } from '@confluence/error-boundary/entry-points/isNotFoundError.ts';
import { isUnauthorizedError } from '@confluence/error-boundary/entry-points/isUnauthorizedError.ts';

import type { ContentPermissionStatus } from '../../contentPermissionStatus.ts';

export type ContentPermissionGateOutcome =
	| 'skipped_permission_denied'
	| 'delayed_capability_loading'
	| 'executed_allowed'
	| 'executed_permission_denied_control'
	| 'executed_permission_loading_control'
	| 'executed_capability_unavailable';

export const getContentPermissionGateOutcome = (
	status: ContentPermissionStatus,
	experimentEnabled: boolean,
): ContentPermissionGateOutcome => {
	switch (status) {
		case 'allowed':
			return 'executed_allowed';
		case 'denied':
			return experimentEnabled ? 'skipped_permission_denied' : 'executed_permission_denied_control';
		case 'loading':
			return experimentEnabled
				? 'delayed_capability_loading'
				: 'executed_permission_loading_control';
		case 'unavailable':
			return 'executed_capability_unavailable';
	}
};

export type RealtimeAccessRequestsErrorReason =
	| 'unauthorized_set_permissions'
	| 'content_not_found'
	| 'validation'
	| 'downstream'
	| 'other';

type ErrorDetails = Error & {
	readonly extensions?: {
		readonly code?: unknown;
		readonly statusCode?: unknown;
		readonly http?: { readonly status?: unknown };
	};
	readonly graphQLErrors?: ReadonlyArray<Error>;
	readonly networkError?: Error | null;
};

const getStatusCode = (error: ErrorDetails): number | undefined => {
	const rawStatus = error.extensions?.statusCode ?? error.extensions?.http?.status;
	const status = typeof rawStatus === 'string' ? Number(rawStatus) : rawStatus;

	return typeof status === 'number' && Number.isFinite(status) ? status : undefined;
};

const hasValidationError = (error: ErrorDetails): boolean => {
	if (getStatusCode(error) === 400 || error.extensions?.code === 'BAD_USER_INPUT') {
		return true;
	}

	return error.graphQLErrors?.some((graphQLError) => hasValidationError(graphQLError)) ?? false;
};

const hasDownstreamError = (error: ErrorDetails): boolean => {
	const statusCode = getStatusCode(error);
	if (error.networkError || (statusCode !== undefined && statusCode >= 500)) {
		return true;
	}

	return error.graphQLErrors?.some((graphQLError) => hasDownstreamError(graphQLError)) ?? false;
};

export const classifyRealtimeAccessRequestsError = (
	error: Error | undefined,
): RealtimeAccessRequestsErrorReason => {
	if (isUnauthorizedError(error)) {
		return 'unauthorized_set_permissions';
	}

	if (isNotFoundError(error)) {
		return 'content_not_found';
	}

	if (error && hasValidationError(error)) {
		return 'validation';
	}

	if (error && hasDownstreamError(error)) {
		return 'downstream';
	}

	return 'other';
};
